import { motion } from "framer-motion";
import { ArrowUpRight, HardDrive, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Eyebrow, MaskLines } from "../components/Motion";

const Tile = ({ className = "", img, tag, title, text, testId, children, i = 0 }) => (
  <motion.article
    data-testid={testId}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 }}
    className={`group relative flex min-h-[280px] flex-col justify-end overflow-hidden border border-white/[0.08] bg-slab/70 p-6 transition-colors duration-500 hover:border-ember/50 md:p-8 ${className}`}
  >
    {img && (
      <>
        <img src={img} alt={title} className="absolute inset-0 h-full w-full object-cover opacity-60 grayscale transition-[transform,opacity,filter] duration-700 group-hover:scale-105 group-hover:opacity-80 group-hover:grayscale-0" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-transparent" />
      </>
    )}
    <div className="relative">
      {children}
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-400">{tag}</p>
      <h3 className="mt-3 font-display text-2xl font-extrabold tracking-tight md:text-3xl">{title}</h3>
      <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-300">{text}</p>
    </div>
  </motion.article>
);

export const Bento = () => (
  <section id="services" data-testid="services-section" className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-36">
    <div className="mb-14 grid gap-8 md:grid-cols-12 md:items-end">
      <div className="md:col-span-7">
        <Eyebrow>01 — What lands on the bench</Eyebrow>
        <MaskLines className="mt-5 font-display text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-5xl lg:text-7xl" lines={["Every fault,", "every machine."]} />
      </div>
      <p className="text-base leading-relaxed text-slate-300 md:col-span-5">
        From cracked laptop screens to desktops that won't power on, every job starts with a diagnosis and a clear quote before any work begins.
      </p>
    </div>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-6 lg:grid-cols-12 lg:grid-rows-[300px_300px_auto]">
      <Tile i={0} testId="bento-laptop" className="md:col-span-6 lg:col-span-7 lg:row-span-2 min-h-[420px]" img="/img/solder.jpg" tag="Laptops" title="Laptop repair" text="Screens, hinges, keyboards, batteries, charging ports, overheating and liquid damage — any make or model." />
      <Tile i={1} testId="bento-desktop" className="md:col-span-3 lg:col-span-5" img="/img/desktop.jpg" tag="Desktops" title="Desktops & gaming PCs" text="Power faults, failed components, upgrades and clean rebuilds." />
      <Tile i={2} testId="bento-board" className="md:col-span-3 lg:col-span-5" img="/img/hero.jpg" tag="Board level" title="Component repair" text="Careful soldering work on boards and connectors." />
      <Tile i={3} testId="bento-data" className="md:col-span-2 lg:col-span-4" tag="Data" title="Data rescue" text="Recovering files from failing or unbootable drives.">
        <HardDrive className="mb-10 h-10 w-10 text-ember" strokeWidth={1.25} />
      </Tile>
      <Tile i={4} testId="bento-parts" className="md:col-span-2 lg:col-span-4" img="/img/laptop.jpg" tag="Parts" title="Upgrades" text="More memory, faster SSDs, fresh thermal paste." />
      <Tile i={5} testId="bento-software" className="md:col-span-2 lg:col-span-4" tag="Software" title="Slow or infected" text="Virus removal, clean installs and tune-ups.">
        <ShieldCheck className="mb-10 h-10 w-10 text-ember" strokeWidth={1.25} />
      </Tile>
    </div>
    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="mt-4">
      <Link to="/gaming-pc" data-testid="bento-gaming" className="group relative flex min-h-[320px] flex-col justify-end overflow-hidden border border-white/[0.08] bg-slab/70 p-6 transition-colors duration-500 hover:border-ember/50 md:min-h-[360px] md:p-10">
        <img src="/img/gaming-tower.jpg" alt="Custom gaming PC" className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-transparent" />
        <div className="relative max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-400">New · Custom builds</p>
          <h3 className="mt-3 font-display text-4xl font-black uppercase leading-[0.92] tracking-tight md:text-6xl">Custom gaming PCs</h3>
          <p className="mt-4 text-sm leading-relaxed text-slate-300 md:text-base">Built to your budget and games, using parts from current Amazon stock. Quoted individually.</p>
          <span className="mt-6 inline-flex items-center gap-2 font-semibold text-ember">See example builds <ArrowUpRight className="h-5 w-5 transition-transform group-hover:rotate-45" /></span>
        </div>
      </Link>
    </motion.div>
    <Link to="/request" data-testid="services-request-btn" className="group mt-10 inline-flex items-center gap-3 font-display text-xl font-bold hover:text-ember">
      Not listed? Describe the fault <ArrowUpRight className="h-5 w-5 transition-transform group-hover:rotate-45" />
    </Link>
  </section>
);
